import { useMemo, useRef } from "react";
import { keyBy, sortBy } from "lodash";
import type { Peer } from "@/react/types/Live/Peer";

interface UsePaginatedSortedPeersOptions {
  handRaisedIds: string[];
  pageSize: number;
  peers: Peer[];
  speakingIds: string[];
}

// Rotates the speakers and peers with hand raised to the first page
// and then sorts the peers based on the rotation
export const usePaginatedSortedPeers = ({
  peers,
  speakingIds,
  handRaisedIds,
  pageSize,
}: UsePaginatedSortedPeersOptions) => {
  // Store the first page rotation
  const firstPageRotationRef = useRef<string[]>([]);
  // Create helper object for easily accessing peers by id
  return useMemo(() => {
    // Create helper object for easily accessing peers by id
    const peersById = keyBy(peers, "id");
    // Update firstPageRotation from peers since tracks may have changed
    const firstPageRotation = firstPageRotationRef.current.filter(
      id => peersById[id],
    );
    const peersWithHandRaisedIds = handRaisedIds.slice(0, pageSize / 2);
    // Make sure speakers and peers with hand raised are part of the rotation
    peersWithHandRaisedIds.concat(speakingIds).forEach(peerID => {
      if (!firstPageRotation.includes(peerID)) {
        // Insert in first position if not part of speakers
        firstPageRotation.unshift(peerID);
      }
      if (firstPageRotation.length > pageSize) {
        // Remove last speaker
        firstPageRotation.pop();
      }
    });

    // Save for next render
    firstPageRotationRef.current = firstPageRotation;
    // Sort peers based on firstPageRotation
    const orderingMap = new Map(
      firstPageRotation.map((id, index) => [id, index + 1]),
    );
    return sortBy(peers, peer => orderingMap.get(peer.id) || Infinity);
  }, [speakingIds, handRaisedIds, pageSize, peers]);
};
